import { useEffect, useRef, useState } from 'react';
import {
  HubConnectionBuilder,
  HubConnectionState,
  type HubConnection,
} from '@microsoft/signalr';
import { useAuth } from '../auth/useAuth';

export interface TrainingProgressEvent {
  loraModelId: string;
  phase: string;
  progress: number | null;
  message: string | null;
  etaSeconds: number | null;
  status?: string;
}

export function useTrainingProgress(loraModelId: string | null | undefined) {
  const { getToken } = useAuth();
  const [event, setEvent] = useState<TrainingProgressEvent | null>(null);
  const [connected, setConnected] = useState(false);
  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    if (!loraModelId) return;
    let cancelled = false;

    (async () => {
      const baseUrl =
        import.meta.env.VITE_SIGNALR_HUB_URL ?? 'http://localhost:5001/hubs';
      const url = `${baseUrl.replace(/\/$/, '')}/generation-status`;
      const token = (await getToken()) ?? '';

      const connection = new HubConnectionBuilder()
        .withUrl(url, { accessTokenFactory: () => token })
        .withAutomaticReconnect()
        .build();

      connection.on('TrainingProgress', (payload: TrainingProgressEvent) => {
        if (!cancelled && payload.loraModelId === loraModelId) {
          setEvent(payload);
        }
      });

      try {
        await connection.start();
        if (cancelled) {
          await connection.stop();
          return;
        }
        await connection.invoke('Subscribe', `lora-training-${loraModelId}`);
        connectionRef.current = connection;
        setConnected(true);
      } catch (err) {
        console.warn('SignalR training progress connection failed', err);
      }
    })();

    return () => {
      cancelled = true;
      const conn = connectionRef.current;
      if (conn && conn.state === HubConnectionState.Connected) {
        conn
          .invoke('Unsubscribe', `lora-training-${loraModelId}`)
          .catch(() => {});
        conn.stop().catch(() => {});
      }
      connectionRef.current = null;
      setConnected(false);
      setEvent(null);
    };
  }, [loraModelId, getToken]);

  return { event, connected };
}
